/**
 * LearnerFrameworkStrip — the "Where am I now?" stack on /learn.
 *
 * P2-lite v2 (May 14, 2026) — broken apart from the previous mega
 * purple slab. Now renders FOUR small, calm, modular cards:
 *
 *   1) CURRENT FOCUS    — stage + one-line context + Open lesson CTA
 *   2) WHY THIS MATTERS — meaning without pressure
 *   3) NEXT ACTION      — the one obvious next step (tappable)
 *   4) WHAT THIS UNLOCKS — progression clarity (optional)
 *
 * Design rules (operator brief, May 14):
 *   - Reduce visual weight dramatically. No giant gradient slab.
 *   - One responsibility per card. Mobile-first. Calm copy.
 *   - Whole card is tappable where it has a destination.
 *   - State resolver underneath is reusable (resolveNextStep).
 *   - If the API hiccups, show ONE calm retry card — sibling
 *     dashboard cards remain unaffected.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Loader2, AlertCircle, RefreshCw, BookOpen, Compass,
  Heart, Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PILLAR_LABEL = {
  stage_mastery: "Skill mastery",
  lessons:       "Lessons logged",
  confidence:    "Confidence checks",
  mindset:       "Mindset (14d)",
  passport:      "Test info",
};

// Pillar → suggested route for the legacy weakest-pillar fallback.
// Branched by track in resolveNextStep() so independents never see
// instructor-only routes like /learn/book.
const NEXT_STEP_BY_PILLAR = {
  stage_mastery: { label: "Book a focused lesson", to: "/learn/book" },
  lessons:       { label: "Book your next lesson", to: "/learn/book" },
  confidence:    { label: "Take a confidence check", to: "/learn" },
  mindset:       { label: "Do today's mindset check-in", to: "/daily-checkin" },
  passport:      { label: "Update your test info", to: "/passport/profile" },
};

// ────────────────────────────────────────────────────────────────────
// Reusable next-step resolver — keep the data brain even when the UI
// surface changes. Returns: {stage, why, action, unlocks, to, source}.
// Independent vs instructor-connected learners get different routes
// at every rule so a Track-C pupil never sees "pair with instructor".
// ────────────────────────────────────────────────────────────────────
function resolveNextStep(data) {
  if (!data) return null;
  const isIndep =
    data.is_independent === true ||
    data.track === "independent" ||
    data.track === "track_c" ||
    data.is_track_c === true;
  const linked = !!data.linked_to_instructor;
  const tr = data.test_readiness_pct ?? 0;
  const lessons = data.lessons_logged_count ?? data.current_stage?.completed_count ?? 0;
  const hasMindsetToday = !!data.mindset_today?.mindset_states?.length;
  const hwActive = data.homework_active?.length || 0;
  const pausedUpload = data.paused_upload || data.resume_upload;
  const hasAnyReview = (data.completed_reviews_count ?? 0) > 0 || !!data.latest_review_id;
  const stage = data.current_stage?.label || "Getting started";

  // 1) Server-supplied next_best_step always wins — but normalise it so
  //    NEXT ACTION never just echoes the stage label.
  if (data.next_best_step?.lesson_id || data.next_best_step?.kind) {
    const nbs = data.next_best_step;
    const nextLessonTitle = data.current_stage?.next_lesson_title;
    let action = nbs.title || "";
    const dupStage = action && (action === stage || /^Stage\s*\d/i.test(action));
    if (!action || dupStage) {
      action = nextLessonTitle ? `Open: ${nextLessonTitle}` : "Continue your stage";
    }
    let unlocks = nbs.unlocks || null;
    if (!unlocks) {
      if (nbs.kind === "stage_lesson") unlocks = "Progress in this stage";
      else if (nbs.kind === "review")  unlocks = "Personalised feedback";
    }
    return {
      stage,
      why: nbs.body || `Move your ${(nbs.kind || "progress").replace(/_/g, " ")} forward`,
      action,
      unlocks,
      to: nbs.link || "/learn",
      source: "server",
    };
  }

  // 2) Paused upload — finish what you started.
  if (pausedUpload) {
    return {
      stage,
      why: "Resuming saves the clips you already uploaded",
      action: "Resume your upload",
      unlocks: "Your AI review",
      to: "/lesson-review",
      source: "paused_upload",
    };
  }

  // 3) Homework waiting.
  if (hwActive > 0) {
    return {
      stage,
      why: "Practising between lessons is where the gains compound",
      action: hwActive === 1 ? "Review your homework" : `Review your ${hwActive} homework items`,
      unlocks: "Confidence and your next lesson",
      to: "/learn/homework",
      source: "homework",
    };
  }

  // 4) First-action gate — no review yet AND no lessons logged.
  if (!hasAnyReview && lessons === 0) {
    if (isIndep) {
      return {
        stage,
        why: "A first AI review gives you a personalised baseline",
        action: "Upload your first drive",
        unlocks: "Personalised feedback + a clear next focus",
        to: "/lesson-review",
        source: "first_review_independent",
      };
    }
    if (!linked) {
      return {
        stage,
        why: "Your instructor unlocks lesson logging and personalised feedback",
        action: "Pair with your instructor",
        unlocks: "Lesson tracking, AI review offers, progression",
        to: "/learn/book",
        source: "pair_instructor",
      };
    }
    return {
      stage,
      why: "Your first lesson sets the baseline everything else builds on",
      action: "Start your first lesson",
      unlocks: "Skill mastery + progression tracking",
      to: "/learn/book",
      source: "first_lesson",
    };
  }

  // 5) No mindset check-in today.
  if (!hasMindsetToday) {
    return {
      stage,
      why: "A 30-second check-in primes how you'll drive today",
      action: "Do today's check-in",
      unlocks: "Adaptive guidance for your next lesson",
      to: "/daily-checkin",
      source: "mindset",
    };
  }

  // 6) Foundation gap — use the calm message the server already produced.
  if (data.foundation_gaps?.length) {
    const g = data.foundation_gaps[0];
    return {
      stage,
      why: g.calm_message || g.reason || "Strengthening foundations now prevents test-day surprises",
      action: "Refresh this foundation",
      unlocks: "Speed + traffic readiness",
      to: g.link || "/learn/lessons",
      source: "foundation_gap",
    };
  }

  // 7) Weakest-pillar heuristic.
  if (data.test_readiness_breakdown?.length) {
    let weakest = null;
    let maxImpact = -1;
    for (const p of data.test_readiness_breakdown) {
      const impact = (p.weight || 0) * (100 - (p.pct || 0));
      if (impact > maxImpact) {
        maxImpact = impact;
        weakest = p;
      }
    }
    if (weakest) {
      const fallback = NEXT_STEP_BY_PILLAR[weakest.id] || { label: "Keep going", to: "/learn" };
      const isInstructorOnlyRoute = fallback.to.startsWith("/learn/book");
      const route = isIndep && isInstructorOnlyRoute ? "/lesson-review" : fallback.to;
      const label = isIndep && isInstructorOnlyRoute ? "Upload another drive" : fallback.label;
      return {
        stage,
        why: `Lifts your ${(PILLAR_LABEL[weakest.id] || weakest.id).toLowerCase()}`,
        action: label,
        unlocks: weakest.id === "stage_mastery" ? "The next responsibility stage" : null,
        to: route,
        source: `weakest_pillar:${weakest.id}`,
      };
    }
  }

  // 8) Genuine all-caught-up — extremely rare; offer self-directed forward motion.
  if (tr >= 80) {
    return {
      stage,
      why: "You're test-ready — keep sharpening the edges",
      action: "Take a mock test",
      unlocks: "Confidence under exam pressure",
      to: "/learn/lessons",
      source: "mock_test_ready",
    };
  }

  // 9) Last-resort — never a dead-end. Self-directed lesson browser.
  return {
    stage,
    why: "Choose any lesson and keep building",
    action: "Browse all lessons",
    unlocks: null,
    to: "/learn/lessons",
    source: "fallback",
  };
}

export default function LearnerFrameworkStrip({
  // Pupil L3 Phase 1 Break #4 (Feb 28, 2026) — when the engine's
  // `where_you_are` slot above already answers "where am I", the
  // strip drops Card 1 ("Current focus" — stage name + progress bar)
  // so the learner never reads the same primary orientation twice.
  // Cards 2/3/4 (Why / Next Action / What this Unlocks) and the
  // "Recent gain" line continue to render — they are supporting
  // continuity, not primary orientation. Default false → original
  // behaviour preserved.
  suppressPrimaryOrientation = false,
} = {}) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await axios.get(
        `${API}/api/learner-framework/me`,
        { withCredentials: true }
      );
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load your readiness right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const next = useMemo(() => resolveNextStep(data), [data]);

  if (loading) {
    return (
      <div
        data-testid="learner-framework-strip-loading"
        className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 shadow-sm"
      >
        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading your focus…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        data-testid="learner-framework-strip-error"
        className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center flex-none">
            <AlertCircle className="w-4 h-4 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400 font-bold">
              Focus paused
            </div>
            <div className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
              We can't load your focus right now
            </div>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
              Tap retry and we'll pull it again.
            </p>
            <button
              type="button"
              data-testid="learner-framework-strip-retry"
              onClick={load}
              className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-bold text-slate-700 dark:text-slate-200 transition"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!next) return null;

  const stageCompleted = data.current_stage?.completed_count ?? 0;
  const stageTotal     = data.current_stage?.lesson_count ?? 0;
  const stagePct       = stageTotal > 0 ? Math.round((stageCompleted / stageTotal) * 100) : 0;
  const nextLessonId   = data.current_stage?.next_lesson_id;
  const nextLessonLink = nextLessonId ? `/lesson/${nextLessonId}` : "/learn/lessons";
  const nextLessonTitle = data.current_stage?.next_lesson_title;

  return (
    <div className="space-y-3">

      {/* Pupil L3.1 Convergence Batch — Break #2: ONE calm "recent gain"
          line above the framework strip. Surfaces a truthful gain
          derived server-side from `skill_progress` (mastered or
          steady-forward). Silent when no qualifying row in the last
          14 days. No counters, no streaks, no fake positivity.
          Phase 5 (Feb 28, 2026) — wording discipline: emoji glyph
          replaced with the Sparkles lucide icon to match the rest of
          the design system. */}
      {data.recent_gain && data.recent_gain.title && (
        <p
          data-testid="learner-framework-recent-gain"
          data-kind={data.recent_gain.kind || ""}
          className="text-xs text-emerald-700 dark:text-emerald-300 leading-relaxed px-1 flex items-center gap-1"
        >
          <Sparkles className="w-3.5 h-3.5 flex-none" aria-hidden="true" />
          <span className="font-bold">Recent gain:</span>{" "}
          {data.recent_gain.kind === "mastered"
            ? `You mastered ${data.recent_gain.title} this week.`
            : `${data.recent_gain.title} is moving forward.`}
        </p>
      )}

      {/* ───────── Card 1 — CURRENT FOCUS ─────────
          Suppressed when the engine's `where_you_are` slot already
          owns primary orientation (Pupil L3 Phase 1 Break #4). */}
      {!suppressPrimaryOrientation && (
      <button
        data-testid="learner-card-current-focus-inner"
        onClick={() => nav(nextLessonLink)}
        className="w-full text-left rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 shadow-sm hover:border-slate-300 dark:hover:border-slate-700 transition-colors group"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center flex-none">
            <Compass className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-bold">
              Current focus
            </div>
            <div
              data-testid="framework-next-step-stage"
              className="text-base font-extrabold text-slate-900 dark:text-white mt-0.5"
            >
              {next.stage}
            </div>
            {nextLessonTitle && (
              <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                Right now: {nextLessonTitle}.
              </p>
            )}
            {stageTotal > 0 && (
              <div className="mt-2.5">
                <div className="flex items-baseline justify-between text-[10px] text-slate-500 dark:text-slate-400">
                  <span>{stageCompleted} of {stageTotal} lessons</span>
                  <span className="font-bold text-slate-600 dark:text-slate-300">{stagePct}%</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full bg-indigo-500/70 transition-all"
                    style={{ width: `${Math.max(2, Math.min(100, stagePct))}%` }}
                  />
                </div>
              </div>
            )}
            <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-indigo-600 dark:text-indigo-300 group-hover:translate-x-0.5 transition-transform">
              <BookOpen className="w-3.5 h-3.5" />
              Open lesson
              <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>
      </button>
      )}

      {/* ───────── Card 2 — WHY THIS MATTERS ───────── */}
      <div
        data-testid="learner-card-why"
        className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center flex-none">
            <Heart className="w-4 h-4 text-rose-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-bold">
              Why this matters
            </div>
            <p
              data-testid="framework-next-step-why"
              className="text-sm text-slate-700 dark:text-slate-200 mt-1 leading-relaxed"
            >
              {next.why}
            </p>
          </div>
        </div>
      </div>

      {/* ───────── Card 3 — NEXT ACTION (tappable) ───────── */}
      <button
        data-testid="framework-next-step"
        data-source={next.source}
        onClick={() => nav(next.to)}
        className="w-full text-left rounded-2xl bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-900/50 p-4 shadow-sm hover:border-emerald-400 dark:hover:border-emerald-700 transition-colors group"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-none">
            <ArrowRight className="w-4 h-4 text-emerald-600 dark:text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400 font-bold">
              Next action
            </div>
            <div
              data-testid="framework-next-step-action"
              className="text-base font-extrabold text-slate-900 dark:text-white mt-0.5"
            >
              {next.action}
            </div>
          </div>
          <div className="flex-none w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center group-hover:scale-105 transition-transform shadow-sm">
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </button>

      {/* ───────── Card 4 — WHAT THIS UNLOCKS (optional) ───────── */}
      {next.unlocks && (
        <div
          data-testid="learner-card-unlocks"
          className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center flex-none">
              <Sparkles className="w-4 h-4 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-bold">
                What this unlocks
              </div>
              <p
                data-testid="framework-next-step-unlocks"
                className="text-sm text-slate-700 dark:text-slate-200 mt-1 leading-relaxed"
              >
                {next.unlocks}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Foundation gap alerts (calm tone, only when present and not already the next step) */}
      {data.foundation_gaps?.length > 0 && next.source !== "foundation_gap" && (
        <div data-testid="foundation-gap-alerts" className="space-y-2">
          {data.foundation_gaps.slice(0, 2).map((g) => (
            <div
              key={g.skill_id}
              data-testid={`foundation-gap-${g.skill_id}`}
              className={`rounded-2xl px-4 py-3 border ${
                g.severity === "high"
                  ? "bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-900/50"
                  : "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900/50"
              }`}
            >
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 dark:text-amber-400 mb-1">
                Foundation refresher
              </div>
              <div className="text-[13px] text-slate-700 dark:text-slate-200 leading-relaxed">
                {g.calm_message || g.reason}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
